import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  PLAN_STATUSES, PLAN_STATUS_LABELS, PLAN_STATUS_COLORS, PLAN_ORIGIN_LABELS,
  PRIORITY_LABELS, PRIORITY_COLORS, PRIORITIES, CATEGORIES, formatDate,
} from "@/lib/labels";
import { Button } from "@/components/ui/button";
import RecordActions from "@/components/RecordActions";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import ReviewDialog from "@/components/ReviewDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardCheck, PlusCircle, Search, ChevronLeft, ChevronRight, Inbox, Zap, AlertTriangle, Target } from "lucide-react";

const ALL = "__all__";

function normId(value) {
  return value === null || value === undefined
    ? ""
    : String(value).trim();
}


function scopeIds(scope, key) {
  const values = Array.isArray(scope?.[key])
    ? scope[key]
    : [];

  return new Set(
    values
      .map(normId)
      .filter(Boolean),
  );
}


function treeIsInScope(
  node,
  scope,
  isGlobal,
) {
  if (isGlobal) {
    return true;
  }

  const clientIds = scopeIds(
    scope,
    "client_ids",
  );
  const unitIds = scopeIds(
    scope,
    "unit_ids",
  );
  const areaIds = scopeIds(
    scope,
    "area_ids",
  );
  const subareaIds = scopeIds(
    scope,
    "subarea_ids",
  );

  const clientId = normId(node?.id);
  if (
    clientIds.size &&
    !clientIds.has(clientId)
  ) {
    return false;
  }

  return true;
}


function filterHierarchyByScope(
  tree,
  scope,
  isGlobal,
) {
  if (
    isGlobal ||
    !Array.isArray(tree)
  ) {
    return Array.isArray(tree)
      ? tree
      : [];
  }

  const clientIds = scopeIds(scope, "client_ids");
  const unitIds = scopeIds(scope, "unit_ids");
  const areaIds = scopeIds(scope, "area_ids");
  const subareaIds = scopeIds(scope, "subarea_ids");

  return tree
    .filter((client) =>
      !clientIds.size ||
      clientIds.has(normId(client?.id)),
    )
    .map((client) => ({
      ...client,
      units: (client?.units || [])
        .filter((unit) =>
          !unitIds.size ||
          unitIds.has(normId(unit?.id)),
        )
        .map((unit) => ({
          ...unit,
          areas: (unit?.areas || [])
            .filter((area) =>
              !areaIds.size ||
              areaIds.has(normId(area?.id)),
            )
            .map((area) => ({
              ...area,
              subareas: (area?.subareas || [])
                .filter((subarea) =>
                  !subareaIds.size ||
                  subareaIds.has(normId(subarea?.id)),
                ),
            })),
        })),
    }));
}



function NewPlanDialog({
  open,
  onOpenChange,
  tree,
  onSaved,
  canCreate,
  scope,
  isGlobal,
}) {
  const [form, setForm] = useState({ title: "", description: "", client: null, unit: null, area: null, subarea: null, category: "", priority: "media", deadline: "" });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const scopedTree = filterHierarchyByScope(
    tree,
    scope,
    isGlobal,
  );
  useEffect(() => { if (open) setForm({ title: "", description: "", client: null, unit: null, area: null, subarea: null, category: "", priority: "media", deadline: "" }); }, [open]);

  const save = async () => {
    if (!canCreate) {
      return toast.error(
        "Você não possui permissão para criar Planos de Ação.",
      );
    }

    if (form.title.trim().length < 3) {
      return toast.error("Informe o título do plano");
    }
    if (!form.client || !form.unit || !form.area || !form.category) return toast.error("Selecione cliente, unidade, área e categoria");
    try {
      const { data } = await api.post("/action-plans", {
        title: form.title, description: form.description,
        client_id: form.client.id, unit_id: form.unit.id, area_id: form.area.id, subarea_id: form.subarea?.id || null,
        category: form.category, priority: form.priority,
        deadline: form.deadline ? `${form.deadline}T23:59:59` : null,
      });
      toast.success(`Plano ${data.number} criado`);
      onOpenChange(false);
      onSaved(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sel = (label, value, items, onVal, testid, placeholder) => (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={value?.id || ""} onValueChange={(v) => onVal(items.find((i) => i.id === v))}>
        <SelectTrigger className="h-9" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="new-plan-dialog">
        <DialogHeader><DialogTitle className="font-heading">Novo Plano de Ação</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Título *</Label>
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Ex.: Eliminação de foco de roedores no armazém" data-testid="plan-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Descrição / objetivo</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} className="resize-none" data-testid="plan-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {sel("Cliente *", form.client, scopedTree, (c) => set({ client: c, unit: null, area: null, subarea: null }), "plan-client-select", "Selecione")}
            {sel("Unidade *", form.unit, form.client?.units || [], (u) => set({ unit: u, area: null, subarea: null }), "plan-unit-select", "Selecione")}
            {sel("Área *", form.area, form.unit?.areas || [], (a) => set({ area: a, subarea: null }), "plan-area-select", "Selecione")}
            {sel("Subárea", form.subarea, form.area?.subareas || [], (s) => set({ subarea: s }), "plan-subarea-select", "Opcional")}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Categoria *</Label>
              <Select value={form.category} onValueChange={(v) => set({ category: v })}>
                <SelectTrigger className="h-9" data-testid="plan-category-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Prioridade</Label>
              <Select value={form.priority} onValueChange={(v) => set({ priority: v })}>
                <SelectTrigger className="h-9" data-testid="plan-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Prazo</Label>
              <Input type="date" value={form.deadline} onChange={(e) => set({ deadline: e.target.value })} className="h-9" data-testid="plan-deadline-input" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            O responsável será atribuído automaticamente pelo mapeamento de Responsáveis do local.
            Prazo vazio = prazo padrão da prioridade.
          </p>

          {!isGlobal && (
            <p className="text-[11px] text-muted-foreground">
              O formulário exibe somente locais dentro do escopo efetivo do usuário.
            </p>
          )}

          <Button
            className="w-full"
            onClick={save}
            disabled={!canCreate}
            data-testid="plan-save-btn"
          >
            Criar Plano de Ação
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <Card className="px-4 py-3 border-border bg-card" data-testid={testid}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`font-heading text-xl font-bold mt-0.5 ${accent || ""}`}>{value}</p>
    </Card>
  );
}

export default function ActionPlans() {
  const navigate = useNavigate();
  const {
    user,
    can,
    isGlobal,
    scope,
  } = useAuth();

  const canView = can("planos", "view");
  const canCreate = can("planos", "create");
  const canEdit = can("planos", "edit");
  const canExecute = can("planos", "execute");
  const canReview = can("planos", "review");
  const canDelete = can("planos", "delete");
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [origin, setOrigin] = useState(ALL);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    if (!canView) {
      setData({ items: [], total: 0, pages: 1 });
      setLoading(false);
      return;
    }

    setLoading(true);
    const params = { page, limit: 10 };
    if (search) params.search = search;
    if (status !== ALL) params.status = status;
    if (category !== ALL) params.category = category;
    if (origin !== ALL) params.origin = origin;
    api.get("/action-plans", { params }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [
    page,
    search,
    status,
    category,
    origin,
    canView,
  ]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, category, origin]);
  useEffect(() => {
    if (!canView) {
      setStats(null);
      setTree([]);
      return;
    }

    api
      .get("/action-plans/stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));

    api
      .get("/hierarchy/tree")
      .then(({ data }) => setTree(data))
      .catch(() => setTree([]));
  }, [canView]);

  const openCount = stats ? ["aberto", "em_analise", "aguardando_aprovacao", "em_execucao", "aguardando_cliente"].reduce((s, k) => s + (stats.by_status[k] || 0), 0) : 0;

  return (
    <div className="space-y-6" data-testid="action-plans-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardCheck size={26} className="text-primary" /> Planos de Ação
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Gestão inteligente de planos — geração automática por reincidência, IA e efetividade.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setDialogOpen(true)} data-testid="new-plan-btn">
            <PlusCircle size={16} className="mr-2" /> Novo Plano
          </Button>
        )}
      </div>

      {!canView ? (
        <Card className="p-5 border-destructive/40 bg-destructive/5">
          <p className="font-medium">
            Acesso ao módulo Plano de Ação não autorizado.
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            A visibilidade desta tela é controlada pela autorização central.
          </p>
        </Card>
      ) : (
        <>
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="plans-kpis">
          <Kpi label="Total de planos" value={stats.total} testid="kpi-total" />
          <Kpi label="Em aberto" value={openCount} accent="text-amber-400" testid="kpi-open" />
          <Kpi label="Concluídos" value={(stats.by_status.concluido || 0) + (stats.by_status.validado || 0)} accent="text-emerald-400" testid="kpi-done" />
          <Kpi label="Atrasados" value={stats.overdue} accent={stats.overdue > 0 ? "text-red-400" : ""} testid="kpi-overdue" />
          <Kpi label="Automáticos" value={stats.auto} accent="text-primary" testid="kpi-auto" />
          <Kpi label="Efetividade" value={stats.effectiveness_rate != null ? `${stats.effectiveness_rate}%` : "—"} accent="text-teal-400" testid="kpi-effectiveness" />
        </div>
      )}
      {stats && (
        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><Target size={13} className="text-primary" />
            Ocorrências com plano: {stats.occurrences_with_plan.count} ({stats.occurrences_with_plan.pct}%)</span>
          {stats.avg_resolution_hours != null && <span>Tempo médio de conclusão: {(stats.avg_resolution_hours / 24).toFixed(1)} dia(s)</span>}
          <span>Manuais: {stats.manual}</span>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por número, título ou responsável..." className="pl-9" data-testid="plans-search-input" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full lg:w-44" data-testid="plans-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos os status</SelectItem>
              {PLAN_STATUSES.map((s) => <SelectItem key={s} value={s}>{PLAN_STATUS_LABELS[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-full lg:w-44" data-testid="plans-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas categorias</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={origin} onValueChange={setOrigin}>
            <SelectTrigger className="w-full lg:w-40" data-testid="plans-filter-origin"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todas origens</SelectItem>
              {Object.entries(PLAN_ORIGIN_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-muted/40">
                <th className="px-4 py-3 font-medium">Plano</th>
                <th className="px-4 py-3 font-medium">Cliente / Unidade</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Categoria</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Responsável</th>
                <th className="px-4 py-3 font-medium">Prioridade</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Prazo</th>
                <th className="px-4 py-3 font-medium">Progresso</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">Carregando...</td></tr>}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground">
                  <Inbox size={28} className="mx-auto mb-2 opacity-40" />Nenhum plano de ação encontrado.
                </td></tr>
              )}
              {!loading && data.items.map((p) => (
                <tr key={p.id} onClick={() => navigate(`/planos-de-acao/${p.id}`)}
                  className="border-b border-border/50 last:border-0 cursor-pointer hover:bg-accent/60 transition-colors"
                  data-testid={`plan-row-${p.number}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs font-semibold text-primary">{p.number}</span>
                      {p.auto_generated && <Zap size={12} className="text-amber-400" title="Gerado automaticamente" />}
                    </div>
                    <p className="text-xs text-muted-foreground truncate max-w-56">{p.title}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{p.client_name}</p>
                    <p className="text-xs text-muted-foreground">{p.unit_name} · {p.area_name}</p>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">{p.category}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-muted-foreground">{p.responsible_name || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={PRIORITY_COLORS[p.priority]}>{PRIORITY_LABELS[p.priority]}</Badge></td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs">
                    <span className={p.overdue ? "text-red-400 font-semibold flex items-center gap-1" : "text-muted-foreground"}>
                      {p.overdue && <AlertTriangle size={11} />}{formatDate(p.deadline).split(" ")[0]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 min-w-24">
                      <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${p.progress?.pct || 0}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground">{p.progress?.pct || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline" className={PLAN_STATUS_COLORS[p.status]}>{PLAN_STATUS_LABELS[p.status]}</Badge></td>
                  <td className="px-2 py-3">
                    <RecordActions
                      module="planos"
                      testId={p.number}
                      onView={() => navigate(`/planos-de-acao/${p.id}`)}
                      onEdit={() => navigate(`/planos-de-acao/${p.id}`)}
                      onReview={() => setReviewTarget(p)}
                      onDelete={() => setDeleteTarget(p)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">Página {data.page || page} de {data.pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="plans-prev-btn"><ChevronLeft size={14} /></Button>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="plans-next-btn"><ChevronRight size={14} /></Button>
          </div>
        </div>
      </div>

      </>
      )}

      <NewPlanDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        tree={tree}
        scope={scope}
        isGlobal={isGlobal}
        canCreate={canCreate}
        onSaved={(id) => navigate(`/planos-de-acao/${id}`)}
      />
      <ReviewDialog open={!!reviewTarget} onOpenChange={(v) => !v && setReviewTarget(null)}
        entity="plano de ação" entityLabel={reviewTarget?.number}
        url={`/action-plans/${reviewTarget?.id}/review`} onDone={load} />
      <ConfirmDeleteDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}
        entity="plano de ação" entityLabel={`${deleteTarget?.number || ""} — ${deleteTarget?.title || ""}`}
        impactUrl={deleteTarget ? `/action-plans/${deleteTarget.id}/delete-impact` : null}
        deleteUrl={`/action-plans/${deleteTarget?.id}`} onDeleted={load} />
    </div>
  );
}
